import React, { forwardRef } from 'react'
import { TextField } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers'
import { Moment } from 'moment'
import * as R from 'ramda'

const useStyles = makeStyles(
  () => ({
    leftArrowButton: {
      position: 'absolute',
      left: 4,
      top: 14,
    },
    rightArrowButton: {
      position: 'absolute',
      right: 4,
      top: 14,
    },
    // This overrides the label style to get 100% of available space
    labelOnTop: {
      minWidth: '138% !important',
    },
    rootLabel: {
      width: '100%',
    },
  }),
  { name: 'Calendar' },
)

export interface CalendarFieldProps<TDate = Moment>
  extends Omit<DatePickerProps<TDate, TDate>, 'value'> {
  readonly error?: boolean
  readonly placeholder?: string
  readonly showTodayButton?: boolean
  readonly showTopPickers?: boolean
  readonly value: TDate | null
}

const CalendarField = forwardRef<HTMLDivElement, CalendarFieldProps>(
  function CalendarField(
    {
      PopperProps,
      showTodayButton,
      showTopPickers,
      error,
      componentsProps: componentsPropsProp,
      ToolbarComponent,
      maxDate,
      renderInput,
      minDate,
      views,
      onChange,
      inputFormat,
      placeholder: placeholderProp,
      ...rest
    },
    ref,
  ) {
    const classes = useStyles()

    const componentsProps = {
      ...componentsPropsProp,
      leftArrowButton: {
        ...componentsPropsProp?.leftArrowButton,
        className: classes.leftArrowButton,
      },
      rightArrowButton: {
        ...componentsPropsProp?.rightArrowButton,
        className: classes.rightArrowButton,
      },
    }
    const remainingProps = R.omit(
      ['onError', 'shouldDisableDate', 'renderDay'],
      rest,
    )

    return (
      <DatePicker
        PopperProps={PopperProps}
        ToolbarComponent={showTopPickers ? ToolbarComponent : undefined}
        componentsProps={componentsProps}
        inputFormat={inputFormat}
        maxDate={maxDate}
        minDate={minDate}
        ref={ref}
        renderInput={
          renderInput ||
          (renderProps => {
            const {
              InputProps,
              inputProps,
              InputLabelProps,
              ...restInputProps
            } = renderProps

            const endAdornment = InputProps?.endAdornment as React.ReactElement

            return (
              <TextField
                fullWidth
                InputLabelProps={{
                  ...InputLabelProps,
                  classes: {
                    root: classes.rootLabel,
                    filled: classes.labelOnTop,
                    focused: classes.labelOnTop,
                  },
                }}
                InputProps={R.omit(['endAdornment'], InputProps)}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                inputProps={{
                  ...inputProps,
                  placeholder: placeholderProp || inputProps?.placeholder,
                }}
                onClick={endAdornment?.props?.children?.props?.onClick}
                {...restInputProps}
                {...remainingProps}
                error={error}
              />
            )
          })
        }
        showToolbar={showTopPickers}
        views={views}
        onChange={onChange}
        {...rest}
      />
    )
  },
)

export default CalendarField
